"use client";

import React from "react";
import { Plane, Globe, Syringe, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "Como funciona o processo de estruturação?",
  answer: "Nosso método baseia-estratégia em três pilares: diagnóstico de marca, planejamento de conteúdo de alto impacto e execução técnica impecável para garantir que sua mensagem chegue ao público certo."
}, {
  question: "Qual o prazo para ver os primeiros resultados?",
  answer: "A estruturação inicial ocorre em poucas semanas, mas o crescimento sustentável é percebido conforme a consistência da sua presença digital se consolida através de vídeos e podcasts profissionais."
}, {
  question: "O estúdio oferece suporte estratégico pós-produção?",
  answer: "Sim, não apenas produzimos o conteúdo, mas orientamos sobre as melhores práticas de distribuição e formatos que performam melhor em cada canal digital específico."
}];
const travelPackFeatures = [{
  icon: Globe,
  text: "Diagnóstico completo da sua marca e definição de posicionamento estratégico."
}, {
  icon: Plane,
  text: "Cronograma de produção otimizado para máxima eficiência de tempo e recursos."
}, {
  icon: Syringe,
  text: "Execução técnica com equipamentos de ponta e direção de cena especializada."
}];
export default function TravelMedicine() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="229915" id="solução" className="bg-background text-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {}
          <div ref={textRef} className={`flex flex-col space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm font-medium">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <span>Estratégia Estruturada</span>
              </div>
              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                A Solução Completa para sua Autoridade Digital
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-default">
                Transformamos sua expertise em conteúdo audiovisual de alto nível. 
                Nossa metodologia foca em criar uma presença digital sólida, 
                profissional e escalável para marcas que buscam liderança.
              </p>
            </div>

            {}
            <div className="bg-card rounded-xl p-6 md:p-8 border border-border/50 shadow-lg shadow-primary/5">
              <h3 className="font-default text-xl font-semibold text-foreground mb-5">
                Pilares da Estruturação
              </h3>
              <ul className="space-y-4">
                {travelPackFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-4">
                    <div className="mt-1 bg-background p-2 rounded-lg shadow-sm">
                      <feature.icon className="w-5 h-5 text-primary stroke-2" />
                    </div>
                    <span className="text-foreground leading-relaxed pt-1 font-default">
                      {feature.text}
                    </span>
                  </li>)}
              </ul>
            </div>

            {}
            <div className="space-y-4">
              <h3 className="font-default text-2xl font-semibold text-foreground">
                Dúvidas Frequentes
              </h3>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i}>
                    <AccordionTrigger className="text-base font-semibold text-foreground hover:text-primary transition-colors font-default">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base font-default">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div className="pt-4">
              <Link to="https://wa.me/5511969100007" newTab>
                <Button size="lg" className="w-full sm:w-auto font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity rounded-full">
                  Entender Minha Estratégia
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative rounded-xl overflow-hidden shadow-[0px_10px_40px_0px_rgba(0,0,0,0.04)] border border-border/20 group bg-white">
              <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors duration-500 z-10 pointer-events-none"></div>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645251/img/growth_trajectory_diagram.png" alt="Diagrama linear mostrando trajetória de crescimento e autoridade digital" className="w-full h-auto object-cover aspect-[4/3] lg:aspect-[3/4] transition-transform duration-700 group-hover:scale-105" />
              
              {}
              <div className="absolute bottom-6 left-6 right-6 md:left-auto md:right-6 md:bottom-8 bg-background/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-border z-20 flex items-center gap-4">
                <div className="bg-secondary p-3 rounded-full">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-default font-bold text-foreground">Crescimento Real</p>
                  <p className="text-sm text-muted-foreground font-default">Trajetória estruturada para sua marca</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}